import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { AlertTriangle, Aperture, CheckCircle2, Loader2, ScanLine, Volume2 } from "lucide-react";
import api from "../lib/api";
import { beepReady, unlockAudio } from "../lib/sound";

const GUIDE_ASPECT = 543 / 400; // optik form kutusu en/boy (BOX_TOP)

function drawOverlay(canvas, video, check, ready) {
  const ctx = canvas.getContext("2d");
  const W = canvas.width, H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  // hedef çerçeve: ortada, form oranında
  let gw = W * 0.9, gh = gw / GUIDE_ASPECT;
  if (gh > H * 0.85) { gh = H * 0.85; gw = gh * GUIDE_ASPECT; }
  const gx = (W - gw) / 2, gy = (H - gh) / 2;
  ctx.fillStyle = "rgba(0,0,0,0.35)";
  ctx.fillRect(0, 0, W, gy); ctx.fillRect(0, gy + gh, W, H - gy - gh);
  ctx.fillRect(0, gy, gx, gh); ctx.fillRect(gx + gw, gy, W - gx - gw, gh);
  const color = ready ? "#10b981" : check?.markers_ok ? "#f59e0b" : "rgba(255,255,255,0.9)";
  ctx.strokeStyle = color; ctx.lineWidth = 4; ctx.lineCap = "round";
  const L = Math.min(gw, gh) * 0.12;
  [[gx, gy, 1, 1], [gx + gw, gy, -1, 1], [gx + gw, gy + gh, -1, -1], [gx, gy + gh, 1, -1]].forEach(([x, y, sx, sy]) => {
    ctx.beginPath(); ctx.moveTo(x, y + sy * L); ctx.lineTo(x, y); ctx.lineTo(x + sx * L, y); ctx.stroke();
  });
  // tespit edilen gerçek köşeler
  if (check?.corners) {
    ctx.fillStyle = ready ? "#10b981" : "#f59e0b";
    ctx.strokeStyle = ctx.fillStyle; ctx.lineWidth = 3;
    const pts = check.corners.map(([nx, ny]) => [nx * W, ny * H]);
    ctx.beginPath(); pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.stroke();
    pts.forEach(([x, y]) => { ctx.beginPath(); ctx.arc(x, y, 10, 0, Math.PI * 2); ctx.fill(); });
  }
}

export function LiveCamera({ open, onOpenChange, onCapture, busy, serial, onSerialChange, lastFeedback, formId = "", hideSerial = false }) {
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const streamRef = useRef(null);
  const loopRef = useRef({ running: false, okStreak: 0, captured: false, beeped: false });
  const [check, setCheck] = useState(null);
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);

  const grab = (maxDim, quality) => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return null;
    const sc = Math.min(1, maxDim / Math.max(v.videoWidth, v.videoHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(v.videoWidth * sc); c.height = Math.round(v.videoHeight * sc);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", quality);
  };

  const capture = () => {
    if (loopRef.current.captured) return;
    const full = grab(3200, 0.95);
    if (!full) return;
    loopRef.current.captured = true;
    onCapture(full);
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loopRef.current = { running: true, okStreak: 0, captured: false, beeped: false };
    unlockAudio();
    setCheck(null); setReady(false); setErr("");
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 3840 }, height: { ideal: 2160 } }, audio: false });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        v.srcObject = stream;
        await v.play();
      } catch (e) {
        setErr("Kamera açılamadı. Tarayıcı kamera iznini verin (HTTPS gerekir) veya 'Fotoğraf Yükle' kullanın.");
        return;
      }
      const tick = async () => {
        if (cancelled || !loopRef.current.running) return;
        const v = videoRef.current, ov = overlayRef.current;
        if (v && ov && v.videoWidth) {
          if (ov.width !== v.clientWidth || ov.height !== v.clientHeight) { ov.width = v.clientWidth; ov.height = v.clientHeight; }
          const small = grab(2000, 0.8);
          if (small && !loopRef.current.captured) {
            try {
              const { data } = await api.post("/optical/check-frame", { image_base64: small, form_id: formId });
              if (cancelled) return;
              setCheck(data);
              loopRef.current.okStreak = data.ready ? loopRef.current.okStreak + 1 : 0;
              const isReady = loopRef.current.okStreak >= 2;
              setReady(isReady);
              drawOverlay(ov, v, data, isReady);
              if (isReady && !loopRef.current.beeped) { loopRef.current.beeped = true; beepReady(); }
              if (!data.ready) loopRef.current.beeped = false;
              if (isReady) capture();
            } catch { /* ağ hatası: bir sonraki karede tekrar */ }
          }
        }
        setTimeout(tick, 350);
      };
      tick();
    })();
    return () => {
      cancelled = true;
      loopRef.current.running = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [open, formId]); // eslint-disable-line react-hooks/exhaustive-deps

  // okuma bitti ama pencere hâlâ açıksa (hata veya seri mod) tekrar denemeye izin ver
  useEffect(() => { if (!busy) { loopRef.current.captured = false; loopRef.current.okStreak = 0; loopRef.current.beeped = false; } }, [busy]);

  const hint = err || check?.hint || "Kamera açılıyor…";
  const tone = ready ? "bg-emerald-500 text-white" : check?.markers_ok ? "bg-amber-500 text-white" : "bg-slate-800 text-white";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-card border-border p-4" data-testid="live-camera-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanLine className="h-5 w-5 text-primary" />Canlı Kamera Rehberi</DialogTitle></DialogHeader>
        <div className={`relative rounded-xl overflow-hidden bg-black transition-all duration-200 ${ready ? "ring-4 ring-emerald-500" : check?.markers_ok ? "ring-4 ring-amber-400" : "ring-1 ring-border"}`} data-testid="live-camera-view">
          <video ref={videoRef} playsInline muted className="w-full max-h-[60vh] object-contain" />
          <canvas ref={overlayRef} className="absolute inset-0 w-full h-full pointer-events-none" />
          {busy && <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-bold gap-2" data-testid="live-camera-busy"><Loader2 className="h-6 w-6 animate-spin" />Okunuyor…</div>}
        </div>
        <div className={`flex items-center justify-center gap-2 rounded-lg font-bold text-sm px-3 py-2.5 ${tone} ${ready ? "animate-pulse" : ""}`} data-testid="live-camera-status">
          {ready ? <CheckCircle2 className="h-5 w-5" /> : <Aperture className="h-5 w-5" />}{hint}
        </div>
        {lastFeedback && (
          <div className={`flex items-center gap-2 rounded-lg text-sm font-semibold px-3 py-2 ${lastFeedback.ok ? "bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-500 text-emerald-800 dark:text-emerald-300" : "bg-amber-50 dark:bg-amber-950/40 border border-amber-500 text-amber-800 dark:text-amber-300"}`} data-testid="live-camera-last">
            {lastFeedback.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}{lastFeedback.text}
          </div>
        )}
        {!hideSerial && (
        <div className="flex items-center gap-2 rounded-lg border border-border px-3 py-2" data-testid="live-camera-serial-row">
          <Switch checked={!!serial} onCheckedChange={onSerialChange} data-testid="live-camera-serial" />
          <div className="text-xs"><span className="font-semibold flex items-center gap-1"><Volume2 className="h-3.5 w-3.5" />Seri Mod (sesli)</span>
            <span className="text-muted-foreground">Hatasız okunan form bip + titreşimle otomatik KAYDEDİLİR, kamera açık kalır → sıradaki forma geçin. Belirsiz işaret varsa uyarı sesi çalar ve kontrol için durur.</span></div>
        </div>
        )}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-muted-foreground">Köşeler sarı → 4 köşe bulundu; yeşil → HAZIR, otomatik çekilir ve AI ile okunur. İstediğiniz an "Şimdi Çek" ile de okutabilirsiniz.</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="live-camera-close">Kapat</Button>
            <Button size="sm" onClick={capture} disabled={busy} data-testid="live-camera-shutter"><Aperture className="h-4 w-4 mr-1" />Şimdi Çek</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
